import { ChangeDetectionStrategy, Component, Input, OnInit, OnChanges, SimpleChanges, signal, WritableSignal } from '@angular/core';
import { IncidentSeverityImpact, IncidentSeverityImpactService, PERSONAL_INJURY_OPTIONS, FINANCIAL_IMPACT_OPTIONS } from 'src/app/api/incident-severity-impact.service';
import { IncidentModalService } from '../../modals/incident-modal.service';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ErrorService } from 'src/app/services/error.service';

@Component({
    selector: 'app-severity-impact-display',
    templateUrl: './severity-impact-display.component.html',
    styleUrls: ['./severity-impact-display.component.scss'],
    providers: [DialogService],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SeverityImpactDisplayComponent implements OnInit, OnChanges {

  @Input()
  workItemId: number = 0;

  severityImpact: WritableSignal<IncidentSeverityImpact | null> = signal(null);
  loading = signal(true);

  personalInjuryOptions = PERSONAL_INJURY_OPTIONS;
  financialImpactOptions = FINANCIAL_IMPACT_OPTIONS;

  constructor(
    private severityImpactService: IncidentSeverityImpactService,
    private incidentModalService: IncidentModalService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private errorService: ErrorService
  ) { }

  ngOnInit(): void {
    if (this.workItemId > 0) {
      this.loadSeverityImpactData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if workItemId has changed
    if (changes['workItemId'] && !changes['workItemId'].firstChange) {
      const currentValue = changes['workItemId'].currentValue;
      const previousValue = changes['workItemId'].previousValue;
      
      // Only reload if the workItemId actually changed
      if (currentValue !== previousValue && currentValue > 0) {
        this.loadSeverityImpactData();
      }
    }
  }

  private loadSeverityImpactData() {
    this.loading.set(true);
    this.severityImpactService.getSeverityImpact(this.workItemId).subscribe({
      next: (data) => {
        this.severityImpact.set(data);
        this.loading.set(false);
      },
      error: (error) => {
        // No severity impact data found, this is fine
        this.severityImpact.set(null);
        this.loading.set(false);
      }
    });
  }

  getPersonalInjuryLabel(level: number): string {
    const option = this.personalInjuryOptions.find(opt => opt.value === level);
    return option ? option.label : 'Unknown';
  }

  getFinancialImpactLabel(level: number): string {
    const option = this.financialImpactOptions.find(opt => opt.value === level);
    return option ? option.label : 'Unknown';
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  onEditClick() {
    this.incidentModalService.openSeverityImpactForm(this.dialogService, this.workItemId, this.severityImpact()).subscribe({
      next: (formData) => {
        if (formData) {
          // Update severity impact data
          this.severityImpactService.updateSeverityImpact(this.workItemId, formData).subscribe({
            next: () => {
              // Reload the data to reflect changes
              this.loadSeverityImpactData();
            },
            error: (err) => {
              this.errorService.error("There was a problem updating the severity impact assessment", err, this.messageService);
            }
          });
        }
      },
      error: (err) => {
        this.errorService.error("There was a problem opening the severity impact form", err, this.messageService);
      }
    });
  }
} 